import React, { useState, useEffect } from 'react';
import './IntakeForm.css';
import { API_ENDPOINTS } from './config/api';
import API_BASE_URL from './config/api';

function IntakeForm({ user, onComplete, onExit }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [formData, setFormData] = useState({
    // About You
    preferredName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: '',
    dateOfBirth: '',
    // Mailing Address
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    
    // Personal Achievements
    greatestAchievement: '',
    nextMilestone: '',
    
    // Self-Assessments
    assessments: {
      insightsDiscovery: 'never',
      cliftonStrengths: 'never',
      myersBriggs: 'never',
      leadershipCircle: 'never',
      discProfile: 'never',
      other: 'never'
    },
    
    // Assessment Results
    assessmentResults: {
      insightsDiscovery: '',
      cliftonStrengths: '',
      myersBriggs: '',
      leadershipCircle: '',
      discProfile: '',
      other: ''
    },
    
    // Other Assessment Name
    otherAssessmentName: '',
    
    // Coaching Goals
    whySeekingCoach: '',
    specificGoals: '',
    currentChallenges: '',
    previousCoaching: false,
    previousCoachingDescription: '',
    
    // Additional Information
    additionalInfo: '',
    coachExpectations: '',
    hobbies: ''
  });

  const totalSteps = 5;

  // Pre-populate form from any existing draft or completed intake
  useEffect(() => {
    const loadDraft = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.INTAKE, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!data.intakeData) return;
        const d = data.intakeData;
        setFormData(prev => ({
          ...prev,
          preferredName: d.preferredName || prev.preferredName,
          lastName: d.lastName || prev.lastName,
          phone: d.phone || '',
          dateOfBirth: d.dateOfBirth ? d.dateOfBirth.split('T')[0] : '',
          address: d.address || '',
          city: d.city || '',
          state: d.state || '',
          zipCode: d.zipCode || '',
          country: d.country || '',
          greatestAchievement: d.greatestAchievement || '',
          nextMilestone: d.nextMilestone || '',
          assessments: d.assessments || prev.assessments,
          assessmentResults: d.assessmentResults || prev.assessmentResults,
          otherAssessmentName: d.otherAssessmentName || '',
          whySeekingCoach: d.whySeekingCoach || '',
          specificGoals: d.specificGoals || '',
          currentChallenges: d.currentChallenges || '',
          previousCoaching: d.previousCoaching ?? false,
          previousCoachingDescription: d.previousCoachingDescription || '',
          additionalInfo: d.additionalInfo || '',
          coachExpectations: d.coachExpectations || '',
          hobbies: d.hobbies || ''
        }));
      } catch (_) {}
    };
    loadDraft();
  }, [user.token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveAndExit = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/intake/draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`
        },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error('Save failed');
      setSaveMsg('Saved!');
      setTimeout(() => {
        if (onExit) onExit();
      }, 600);
    } catch (_) {
      setSaveMsg('Could not save. Please try again.');
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return formData.preferredName.trim() && formData.lastName.trim();
      case 2:
        return true; // Personal Achievements are now optional
      case 3:
        return true; // Assessment step is always valid
      case 4:
        return true; // Coaching goals are now optional
      case 5:
        return true; // Additional information is now optional
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (!validateCurrentStep()) {
      alert('Please fill in all required fields before continuing.');
      return;
    }
    
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) {
      alert('Please fill in all required fields before completing.');
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.INTAKE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          ...formData,
          assessmentResults: formData.assessmentResults
        })
      });

      if (response.ok) {
        alert('Intake form submitted successfully! Welcome to NextLeader Coaching!');
        onComplete();
      } else {
        const errorData = await response.json();
        console.error('Failed to submit intake form:', errorData);
        alert(`Failed to submit intake form: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error submitting intake form:', error);
      alert('Network error while submitting form. Please check your connection and try again.');
    }
  };

  const assessmentOptions = [
    { value: 'never', label: 'Never taken' },
    { value: 'recent', label: 'Taken in last 18 months' },
    { value: 'old', label: 'Taken longer than 18 months ago' },
    { value: 'unsure', label: 'Unsure' }
  ];

  const assessmentTypes = [
    { key: 'insightsDiscovery', label: 'Insights Discovery' },
    { key: 'cliftonStrengths', label: 'Clifton Strengths' },
    { key: 'myersBriggs', label: 'Myers-Briggs' },
    { key: 'leadershipCircle', label: 'Leadership Circle Profile' },
    { key: 'discProfile', label: 'DISC Profile' },
    { key: 'other', label: 'Other' }
  ];

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <h2>About You</h2>
            <p className="step-description">Let's start with some basic information about yourself.</p>
            
            <div className="form-row">
              <div className="form-group">
                <label>Preferred Name *</label>
                <input
                  type="text"
                  value={formData.preferredName}
                  onChange={(e) => handleChange('preferredName', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Date of Birth</label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Mailing Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="Street address"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>State/Province</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>ZIP/Postal Code</label>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => handleChange('zipCode', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => handleChange('country', e.target.value)}
                  placeholder="United States"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <h2>Personal Achievements</h2>
            <p className="step-description">Tell us about your journey and aspirations.</p>
            
            <div className="form-group">
              <label>What has been your greatest achievement to date?</label>
              <textarea
                value={formData.greatestAchievement}
                onChange={(e) => handleChange('greatestAchievement', e.target.value)}
                rows="4"
                placeholder="Share your proudest moment or biggest accomplishment..."
              />
            </div>

            <div className="form-group">
              <label>What is the next major milestone you're looking forward to?</label>
              <textarea
                value={formData.nextMilestone}
                onChange={(e) => handleChange('nextMilestone', e.target.value)}
                rows="4"
                placeholder="Describe your next big goal or aspiration..."
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <h2>Self-Assessments</h2>
            <p className="step-description">Let us know which assessments you've taken and when.</p>
            
            <div className="assessments-grid">
              {assessmentTypes.map(assessment => {
                // Special handling for "Other" assessment
                const isOtherAssessment = assessment.key === 'other';
                const otherAssessmentOptions = isOtherAssessment 
                  ? assessmentOptions.map(option => 
                      option.value === 'never' 
                        ? { ...option, label: 'N/A' }
                        : option
                    )
                  : assessmentOptions;

                return (
                  <div key={assessment.key} className="assessment-row">
                    <div className="assessment-label">
                      {isOtherAssessment ? (
                        <div className="other-assessment-header">
                          <span>Other Assessment</span>
                          <input
                            type="text"
                            value={formData.otherAssessmentName}
                            onChange={(e) => handleChange('otherAssessmentName', e.target.value)}
                            placeholder="Please specify the assessment name..."
                            className="other-assessment-name"
                          />
                        </div>
                      ) : (
                        assessment.label
                      )}
                    </div>
                    <div className="assessment-options">
                      {otherAssessmentOptions.map(option => (
                        <label key={option.value} className="radio-option">
                          <input
                            type="radio"
                            name={assessment.key}
                            value={option.value}
                            checked={formData.assessments[assessment.key] === option.value}
                            onChange={(e) => handleChange(`assessments.${assessment.key}`, e.target.value)}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                    
                    {formData.assessments[assessment.key] !== 'never' && (
                      <div className="assessment-results">
                        <label>Results/Details (if available):</label>
                        <textarea
                          value={formData.assessmentResults[assessment.key]}
                          onChange={(e) => handleChange(`assessmentResults.${assessment.key}`, e.target.value)}
                          rows="2"
                          placeholder="Please share your results, key insights, or details from this assessment..."
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <h2>Coaching Goals</h2>
            <p className="step-description">Help us understand what you're looking to achieve through coaching.</p>
            
            <div className="form-group">
              <label>Have you worked with a coach before?</label>
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    value={true}
                    checked={formData.previousCoaching === true}
                    onChange={(e) => handleChange('previousCoaching', true)}
                  />
                  <span>Yes</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    value={false}
                    checked={formData.previousCoaching === false}
                    onChange={(e) => handleChange('previousCoaching', false)}
                  />
                  <span>No</span>
                </label>
              </div>
            </div>

            {formData.previousCoaching && (
              <div className="form-group">
                <label>Please describe your previous coaching experience</label>
                <textarea
                  value={formData.previousCoachingDescription}
                  onChange={(e) => handleChange('previousCoachingDescription', e.target.value)}
                  rows="3"
                  placeholder="Tell us about your previous coaching experience..."
                />
              </div>
            )}

            <div className="form-group">
              <label>Why are you seeking a coach?</label>
              <input
                type="text"
                value={formData.whySeekingCoach}
                onChange={(e) => handleChange('whySeekingCoach', e.target.value)}
                placeholder="What motivated you to seek coaching?"
              />
            </div>

            <div className="form-group">
              <label>What are your specific coaching goals?</label>
              <textarea
                value={formData.specificGoals}
                onChange={(e) => handleChange('specificGoals', e.target.value)}
                rows="4"
                placeholder="Describe what you hope to accomplish..."
              />
            </div>

            <div className="form-group">
              <label>What are your current challenges or obstacles?</label>
              <textarea
                value={formData.currentChallenges}
                onChange={(e) => handleChange('currentChallenges', e.target.value)}
                rows="4"
                placeholder="What barriers are you facing?"
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="step-content">
            <h2>Additional Information</h2>
            <p className="step-description">Help us get to know you better.</p>
            
            <div className="form-group">
              <label>Is there anything else you'd like to share about your current situation?</label>
              <textarea
                value={formData.additionalInfo}
                onChange={(e) => handleChange('additionalInfo', e.target.value)}
                rows="4"
                placeholder="Share any additional context that might be helpful..."
              />
            </div>

            <div className="form-group">
              <label>What are your expectations for your coach?</label>
              <textarea
                value={formData.coachExpectations}
                onChange={(e) => handleChange('coachExpectations', e.target.value)}
                rows="4"
                placeholder="What qualities or approaches are you looking for in a coach?"
              />
            </div>

            <div className="form-group">
              <label>What are your hobbies or interests outside of work?</label>
              <textarea
                value={formData.hobbies}
                onChange={(e) => handleChange('hobbies', e.target.value)}
                rows="3"
                placeholder="Tell us what you enjoy doing in your free time..."
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="intake-container">
      <div className="intake-card">
        <div className="intake-header">
          {onExit && (
            <div className="intake-exit-row">
              <button
                type="button"
                className="intake-save-exit-btn"
                onClick={handleSaveAndExit}
                disabled={saving}
              >
                {saving ? 'Saving…' : saveMsg || 'Save & Exit'}
              </button>
            </div>
          )}
          <a href="https://www.nextleadercoaching.com" target="_blank" rel="noopener noreferrer">
            <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" className="intake-logo" />
          </a>
          <h1>Welcome to NextLeader Coaching!</h1>
          <p>Before we begin your coaching journey, we'd like to learn more about you.</p>
        </div>

        <div className="progress-bar">
          <div className="progress-steps">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i + 1}
                className={`step ${i + 1 <= currentStep ? 'active' : ''} ${i + 1 < currentStep ? 'completed' : ''}`}
              >
                {i + 1}
              </div>
            ))}
          </div>
          <div className="progress-text">
            Step {currentStep} of {totalSteps}
          </div>
        </div>

        <form className="intake-form">
          {renderStep()}

          <div className="form-actions">
            {currentStep > 1 && (
              <button type="button" onClick={prevStep} className="btn-secondary">
                Previous
              </button>
            )}
            
            {currentStep < totalSteps ? (
              <button type="button" onClick={nextStep} className="btn-primary">
                Next Step
              </button>
            ) : (
              <button type="button" onClick={handleSubmit} className="btn-primary">
                Complete Intake
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default IntakeForm;